import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'

export interface ConfirmOptions {
  title: string
  body?: string
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
}

type ConfirmRequest = {
  opts: ConfirmOptions
  resolve: (value: boolean) => void
}

const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | undefined>(
  undefined
)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ConfirmRequest | null>(null)
  // Mirrors `request` so confirm/settle can resolve the in-flight promise
  // without stale closures (and stay referentially stable via useCallback).
  const requestRef = useRef<ConfirmRequest | null>(null)

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      // A second confirm() while one is open supersedes it: settle the
      // previous request as false so its promise never dangles.
      requestRef.current?.resolve(false)
      const next: ConfirmRequest = { opts, resolve }
      requestRef.current = next
      setRequest(next)
    })
  }, [])

  const settle = useCallback((value: boolean) => {
    requestRef.current?.resolve(value)
    requestRef.current = null
    setRequest(null)
  }, [])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request && (
        <ConfirmDialogInner
          opts={request.opts}
          onSettle={settle}
        />
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider')
  return ctx
}

// ────────────────────────────────────────────────────────────────────────────

interface ConfirmDialogInnerProps {
  opts: ConfirmOptions
  onSettle: (value: boolean) => void
}

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => !el.hasAttribute('disabled'))
}

function ConfirmDialogInner({ opts, onSettle }: ConfirmDialogInnerProps) {
  // The provider stays i18n-agnostic: every caller passes already-translated
  // copy. Only the two DEFAULT button labels are translated here, since no
  // call site supplies them.
  const { t } = useTranslation()
  const {
    title,
    body,
    confirmLabel = t('common.confirm'),
    cancelLabel = t('common.cancel'),
    danger = false,
  } = opts

  const dialogRef = useRef<HTMLDivElement>(null)
  const cancelBtnRef = useRef<HTMLButtonElement>(null)
  // Track the element that had focus before the dialog opened
  const previousFocusRef = useRef<HTMLElement | null>(
    typeof document !== 'undefined' ? (document.activeElement as HTMLElement) : null
  )

  // Initial focus on Cancel button
  useEffect(() => {
    cancelBtnRef.current?.focus()
  }, [])

  // Restore focus on unmount
  useEffect(() => {
    return () => {
      previousFocusRef.current?.focus()
    }
  }, [])

  // Escape key handler
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onSettle(false)
        return
      }
      // Focus trap
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = getFocusable(dialogRef.current)
        if (focusable.length === 0) return
        // Guarded by the length check above — both ends exist.
        const first = focusable[0]!
        const last = focusable[focusable.length - 1]!
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault()
            last.focus()
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault()
            first.focus()
          }
        }
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onSettle])

  return (
    <>
      {/* Overlay */}
      <div
        data-testid="confirm-overlay"
        className="fixed inset-0 z-50 bg-black/50"
        onClick={() => onSettle(false)}
        aria-hidden="true"
      />
      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby={body ? 'confirm-dialog-body' : undefined}
        className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg border border-stone-200 bg-white p-6 shadow-lg dark:border-stone-700 dark:bg-stone-900"
      >
        <h2
          id="confirm-dialog-title"
          className="text-sm font-semibold text-stone-900 dark:text-stone-50"
        >
          {title}
        </h2>
        {body && (
          <p id="confirm-dialog-body" className="mt-2 text-sm text-stone-600 dark:text-stone-400">
            {body}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            ref={cancelBtnRef}
            data-testid="cancel-btn"
            type="button"
            onClick={() => onSettle(false)}
            className="rounded-md border border-stone-200 bg-white px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-200 dark:hover:bg-stone-700"
          >
            {cancelLabel}
          </button>
          <button
            data-testid="confirm-btn"
            type="button"
            onClick={() => onSettle(true)}
            className={
              danger
                ? 'rounded-md px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700'
                : 'rounded-md px-3 py-1.5 text-sm font-medium text-white bg-stone-800 hover:bg-stone-700 dark:bg-stone-200 dark:text-stone-900 dark:hover:bg-stone-300'
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </>
  )
}
